import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import * as statisticsApi from '../../api/statistics';

// Async thunks
export const fetchStatisticsThunk = createAsyncThunk(
  'statistics/fetch',
  async ({ year, week }, { rejectWithValue }) => {
    try {
      return await statisticsApi.fetchStatistics(year, week);
    } catch (error) {
      return rejectWithValue(error.message || `Failed to fetch statistics for year ${year}, week ${week}`);
    }
  }
);

export const recalculateWeekStatisticsThunk = createAsyncThunk(
  'statistics/recalculate',
  async ({ year, week, onStart, onSuccess, onError }, { rejectWithValue, dispatch }) => {
    try {
      // Call onStart callback if provided
      if (onStart && typeof onStart === 'function') {
        onStart(year, week);
      }

      const result = await statisticsApi.recalculateWeekStatistics(year, week);

      // Refresh the statistics after recalculation
      dispatch(fetchStatisticsThunk({ year, week }));

      // Call onSuccess callback if provided
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(result);
      }

      return result;
    } catch (error) {
      // Call onError callback if provided
      if (onError && typeof onError === 'function') {
        onError(error.message || `Failed to recalculate statistics for year ${year}, week ${week}`);
      }

      return rejectWithValue(error.message || `Failed to recalculate statistics for year ${year}, week ${week}`);
    }
  }
);

export const fetchAvailableWeeksThunk = createAsyncThunk(
  'statistics/fetchAvailableWeeks',
  async (_, { rejectWithValue }) => {
    try {
      return await statisticsApi.fetchAvailableWeeks();
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch available weeks');
    }
  }
);

// Initial state
const initialState = {
  data: null,
  availableWeeks: [],
  loading: false,
  error: null,
  recalculating: false,
  currentYear: null,
  currentWeek: null
};

// Statistics slice
const statisticsSlice = createSlice({
  name: 'statistics',
  initialState,
  reducers: {
    clearStatisticsErrors: (state) => {
      state.error = null;
    },
    setCurrentPeriod: (state, action) => {
      state.currentYear = action.payload.year;
      state.currentWeek = action.payload.week;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch statistics
      .addCase(fetchStatisticsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatisticsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchStatisticsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Recalculate week statistics
      .addCase(recalculateWeekStatisticsThunk.pending, (state) => {
        state.recalculating = true;
        state.error = null;
      })
      .addCase(recalculateWeekStatisticsThunk.fulfilled, (state) => {
        state.recalculating = false;
        // Data will be updated by the fetchStatisticsThunk that is dispatched after recalculation
      })
      .addCase(recalculateWeekStatisticsThunk.rejected, (state, action) => {
        state.recalculating = false;
        state.error = action.payload;
      })

      // Fetch available weeks
      .addCase(fetchAvailableWeeksThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableWeeksThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.availableWeeks = action.payload;
      })
      .addCase(fetchAvailableWeeksThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// Export actions
export const { clearStatisticsErrors, setCurrentPeriod } = statisticsSlice.actions;

// Selectors
export const selectStatisticsData = (state) => state.statistics.data;
export const selectAvailableWeeks = (state) => state.statistics.availableWeeks;
export const selectStatisticsLoading = (state) => state.statistics.loading;
export const selectStatisticsError = (state) => state.statistics.error;
export const selectIsRecalculating = (state) => state.statistics.recalculating;

// Memoized selector for current period
export const selectCurrentPeriod = createSelector(
  [(state) => state.statistics.currentYear, (state) => state.statistics.currentWeek],
  (year, week) => ({
    year,
    week
  })
);

export default statisticsSlice.reducer;
